import { useState } from 'react'
import { Box, Button, IconButton, Tooltip } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'

interface SqlBlockProps {
  sql: string
  /** Collapse to this many lines, with a "Show full SQL" button when it is longer. */
  maxLines?: number
}

/**
 * A SQL statement, monospaced and copyable.
 *
 * Wraps rather than scrolls horizontally. A horizontally scrolling code block inside a table cell
 * inside a page is three nested scroll regions, and on a phone it is impossible to read any of them.
 */
export function SqlBlock({ sql, maxLines = 4 }: SqlBlockProps) {
  const [expanded, setExpanded] = useState(false)
  const [copied, setCopied] = useState(false)
  // A cheap guess at whether the clamp hides anything, so short statements do not get a pointless
  // toggle. Long single lines wrap, so count characters as well as newlines.
  const clampable = sql.split('\n').length > maxLines || sql.length > maxLines * 70

  const copy = async (event: React.MouseEvent) => {
    event.stopPropagation()
    try {
      await navigator.clipboard.writeText(sql)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard access is refused in some contexts and there is nothing useful to say about it;
      // the text is on screen and selectable either way.
    }
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1,
          pr: 5,
          borderRadius: 1,
          bgcolor: 'action.hover',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: 12,
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: expanded ? 'unset' : maxLines,
        }}
      >
        {sql}
      </Box>

      <Tooltip title={copied ? 'Copied' : 'Copy SQL'}>
        <IconButton
          size="small"
          onClick={copy}
          sx={{ position: 'absolute', top: 4, right: 4 }}
          aria-label="Copy SQL"
        >
          {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
        </IconButton>
      </Tooltip>

      {clampable && (
        <Button
          size="small"
          onClick={(event) => {
            // Rows that hold a SqlBlock are often clickable themselves.
            event.stopPropagation()
            setExpanded((v) => !v)
          }}
          aria-expanded={expanded}
          sx={{ mt: 0.25, px: 0.5, minHeight: 32 }}
        >
          {expanded ? 'Show less' : 'Show full SQL'}
        </Button>
      )}
    </Box>
  )
}
